import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Trash2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const FILTERS = ["All", "Ohio Medicaid", "Private pay", "Not sure yet"];

const fmt = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
};

export default function Admin() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("All");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/submissions");
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) {
        try {
          await api.post("/auth/refresh");
          const { data } = await api.get("/admin/submissions");
          setRows(Array.isArray(data) ? data : []);
          return;
        } catch {
          nav("/admin/login");
          return;
        }
      }
      toast.error(formatApiError(err));
      setRows([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shown = useMemo(() => (rows || []).filter((r) => filter === "All" || r.payment === filter), [rows, filter]);

  const setStatus = async (id, status) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    try { await api.patch(`/admin/submissions/${id}`, { status }); } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try { await api.delete(`/admin/submissions/${id}`); setRows((rs) => rs.filter((r) => r.id !== id)); } catch (err) { toast.error(formatApiError(err)); }
  };
  const logout = async () => { await api.post("/auth/logout").catch(() => {}); nav("/admin/login"); };

  if (rows === null) return <main className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></main>;

  return (
    <main id="main-content" className="min-h-screen bg-[#F4F1EA] pb-20 pt-32" data-testid="admin-page">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono-ui text-xs uppercase tracking-[0.2em] text-[#A9533A]">Private</p>
            <h1 className="font-serif-display mt-2 text-5xl">Contact requests</h1>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-[#D6CFC1] px-4 py-2 text-sm hover:bg-[#FDFBF7]" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
        <div className="mt-8 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`admin-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`rounded-full px-4 py-2 text-sm transition-colors ${filter === f ? "bg-[#1E2621] text-[#F4F1EA]" : "border border-[#D6CFC1] bg-[#FDFBF7] text-[#1E2621]"}`}>
              {f} <span className="opacity-70">({(rows || []).filter((r) => f === "All" || r.payment === f).length})</span>
            </button>
          ))}
        </div>
        <div className="mt-6 overflow-x-auto rounded-2xl border border-[#E2DCD0] bg-[#FDFBF7]">
          <table className="w-full min-w-[900px] text-left text-sm" data-testid="admin-submissions-table">
            <thead className="bg-[#FAF8F4] font-mono-ui text-xs uppercase tracking-[0.12em] text-[#4A554D]">
              <tr>{["Received", "Name", "Contact", "Service", "Pay", "Message", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
            </thead>
            <tbody>
              {shown.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#4A554D]" data-testid="admin-empty">No submissions yet.</td></tr>}
              {shown.map((r) => (
                <tr key={r.id} className="border-t border-[#E2DCD0] align-top" data-testid={`admin-row-${r.id}`}>
                  <td className="whitespace-nowrap px-4 py-4 text-[#4A554D]">{fmt(r.created_at)}</td>
                  <td className="px-4 py-4 font-medium">{r.name}</td>
                  <td className="px-4 py-4"><div>{r.email}</div><div className="text-[#4A554D]">{r.phone}</div><div className="text-xs text-[#4A554D]">Prefers {r.preferred_contact}</div></td>
                  <td className="px-4 py-4">{r.service}</td>
                  <td className="px-4 py-4">{r.payment}</td>
                  <td className="max-w-xs px-4 py-4 text-[#4A554D]">{r.message}</td>
                  <td className="px-4 py-4">
                    <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="rounded-lg border border-[#D6CFC1] bg-white px-2 py-1.5" data-testid={`admin-status-${r.id}`}>
                      <option value="new">New</option><option value="contacted">Contacted</option><option value="closed">Closed</option>
                    </select>
                  </td>
                  <td className="px-4 py-4"><button onClick={() => remove(r.id)} className="rounded-full p-2 text-[#A9533A] hover:bg-[#FFF8F0]" aria-label="Delete" data-testid={`admin-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}
